/**
 * <MappingQueue country canEdit skus onChanged />  - the "Needs mapping" section of the Code Mappings page.
 *
 * GET /api/sku-aliases/queue: every code that matched no SKU, from API sales, Amazon returns and Retail POs,
 * one row per code, ranked by the money it holds. Each row shows the sources, units, ₹, dates, the ranked SKU
 * suggestion with Accept (components/CodeSuggestion), a manual SKU picker, and Ignore (not a product).
 * Bulk bar: select rows → "Map selected to one SKU" / "Ignore selected"; "Accept all strong suggestions" maps
 * every row whose best suggestion is a rule match or strong, after showing the count. Everything goes through
 * POST /accept-many so the waiting sales, returns and PO lines move in once.
 * 50 rows per page. "Export for executives" downloads the queue as Excel with category and brand (one sheet per
 * category, or one category's file); the filled file goes back in through Code Mappings → Upload filled file.
 * `version` (a number) reloads the queue when the page changes mappings another way (e.g. a file upload).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Ban, CheckCheck, Download, RefreshCw, Undo2 } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { ConfirmDialog } from './ConfirmDialog';
import { LoadError, errorMessage } from './LoadError';
import { StatStrip } from './StatStrip';
import { TablePager, useTableControls } from './TableControls';
import { downloadFile } from '../lib/download';
import { SuggestionCell } from './CodeSuggestion';
import { SkuPicker } from './sales/SalesCodeMatching';
import { platformLabel } from '../config/platforms';

import { useChannels } from '../hooks/useChannels';
const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/sku-aliases`;
const SOURCE_LABEL = { sales: 'Amazon sales', returns: 'Amazon returns', po: 'Retail PO' };
const SOURCE_TONE = { sales: 'bg-amber-50 text-amber-800 ring-amber-200', returns: 'bg-rose-50 text-rose-800 ring-rose-200', po: 'bg-blue-50 text-blue-800 ring-blue-200' };
const inr = value => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
const STRONG = new Set(['rule', 'high']);

const SourceChip = ({ source }) => (
  <span className={`inline-flex whitespace-nowrap rounded-full px-1.5 text-[10px] font-medium ring-1 ring-inset ${SOURCE_TONE[source] || 'bg-gray-100 text-gray-700 ring-gray-200'}`}>{SOURCE_LABEL[source] || source}</span>
);

const dates = row => (!row.first_date ? '—' : row.first_date === row.last_date ? row.first_date : `${row.first_date} → ${row.last_date}`);

export const MappingQueue = ({ country, canEdit, skus, onChanged, initialSource = '', version = 0 }) => {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [query, setQuery] = useState('');
  const [source, setSource] = useState(initialSource);
  const [onlySuggested, setOnlySuggested] = useState(false);
  const [showIgnored, setShowIgnored] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkSku, setBulkSku] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState(null); // a row, or null for "selected"
  const [ignoreReason, setIgnoreReason] = useState('');
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/queue`, { params: { country, include_ignored: showIgnored || undefined }, headers: getAuthHeaders() })
      .then(res => { setData(res.data); setError(null); })
      .catch(err => setError(err))
      .finally(() => setLoading(false));
  }, [country, showIgnored]);
  useEffect(load, [load, version]);
  useEffect(() => { setSource(initialSource); }, [initialSource]);
  // Only codes still in the queue can stay selected (after a reload, a country change or a filter change).
  useEffect(() => {
    const present = new Set((data?.codes || []).filter(row => !row.ignored).map(row => row.code));
    setSelected(prev => (Array.from(prev).every(code => present.has(code)) ? prev : new Set(Array.from(prev).filter(code => present.has(code)))));
  }, [data]);
  const changed = () => { setSelected(new Set()); load(); onChanged?.(); };

  const rows = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (data?.codes || []).filter(row => (!source || row.sources.includes(source))
      && (!onlySuggested || (row.suggestions || []).length)
      && (!term || `${row.code} ${(row.alt_codes || []).join(' ')} ${row.title || ''} ${(row.pos || []).join(' ')}`.toLowerCase().includes(term)));
  }, [data, query, source, onlySuggested]);
  const table = useTableControls(rows, { pageSize: 50 });   // order comes from the server (largest value first)
  const { setPage } = table;
  useEffect(() => { setPage(1); }, [query, source, onlySuggested, showIgnored, setPage]);
  const strongRows = useMemo(() => rows.filter(row => !row.ignored && STRONG.has(row.best_confidence)), [rows]);
  const totals = data?.totals || {};
  const live = rows.filter(row => !row.ignored);
  const chosen = live.filter(row => selected.has(row.code));   // selected AND visible: what the bulk actions act on
  const pageLive = table.pageRows.filter(row => !row.ignored);
  const allSelected = pageLive.length > 0 && pageLive.every(row => selected.has(row.code));
  const toggle = code => setSelected(prev => { const next = new Set(prev); if (next.has(code)) next.delete(code); else next.add(code); return next; });
  // The header box selects (or clears) this page's rows; selections on other pages stay.
  const toggleAll = () => setSelected(prev => {
    const next = new Set(prev);
    pageLive.forEach(row => (allSelected ? next.delete(row.code) : next.add(row.code)));
    return next;
  });

  const acceptMany = async (items, label) => {
    if (!items.length) { toast.info('Nothing selected in the current list'); return; }
    setBusy(label);
    try {
      // The API takes up to 500 codes per call.
      const saved = [], failed = [];
      let movedSales = 0, matchedReturns = 0, matchedPo = 0;
      for (let start = 0; start < items.length; start += 500) {
        const { data: result } = await axios.post(`${API}/accept-many`, { items: items.slice(start, start + 500), country }, { headers: getAuthHeaders() });
        saved.push(...(result.saved || [])); failed.push(...(result.failed || []));
        movedSales += result.moved_sales || 0; matchedReturns += result.matched_returns || 0; matchedPo += result.matched_po_lines || 0;
      }
      const message = `${saved.length} code${saved.length === 1 ? '' : 's'} mapped${failed.length ? `, ${failed.length} skipped` : ''}. ${movedSales} sales line(s), ${matchedReturns} return row(s) and ${matchedPo} PO line(s) moved in.`;
      if (failed.length) toast.warning(message, { description: failed.slice(0, 3).map(f => `${f.code}: ${f.error}`).join(' · ') });
      else toast.success(message);
      changed();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the mappings'));
    } finally {
      setBusy('');
    }
  };
  const acceptAllStrong = () => acceptMany(strongRows.map(row => ({ code: row.code, sku_code: row.suggestions[0].sku_code, asins: row.asins || [] })), 'accept-all');
  const mapSelected = () => {
    const items = chosen.map(row => ({ code: row.code, sku_code: bulkSku, asins: row.asins || [] }));
    setBulkOpen(false);
    return acceptMany(items, 'map-selected');
  };
  const ignore = async () => {
    const codes = ignoreTarget ? [ignoreTarget.code] : chosen.map(row => row.code);
    if (!codes.length) { toast.info('Nothing selected in the current list'); setIgnoreOpen(false); return; }
    setBusy('ignore');
    // One request per code; every outcome is reported and the list reloads whatever happened.
    const results = await Promise.allSettled(codes.map(code => axios.post(`${API}/ignored`, { code, country, reason: ignoreReason }, { headers: getAuthHeaders() })));
    const failed = results.map((r, i) => (r.status === 'rejected' ? `${codes[i]}: ${errorMessage(r.reason, 'failed')}` : null)).filter(Boolean);
    const done = codes.length - failed.length;
    if (failed.length) toast.warning(`${done} of ${codes.length} ignored`, { description: failed.slice(0, 3).join(' · ') });
    else toast.success(codes.length === 1 ? `${codes[0]} will stay out of the queue` : `${codes.length} codes will stay out of the queue`);
    setIgnoreOpen(false); setIgnoreReason(''); setIgnoreTarget(null);
    setBusy('');
    changed();
  };
  const unignore = async row => {
    try {
      await axios.post(`${API}/ignored/remove`, null, { params: { country, code: row.code }, headers: getAuthHeaders() });
      toast.success(`${row.code} is back in the queue`);
      changed();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not un-ignore the code'));
    }
  };
  const recheck = async () => {
    setBusy('recheck');
    try {
      const { data: result } = await axios.post(`${API}/queue/recheck`, null, { params: { country }, headers: getAuthHeaders() });
      toast.success(result.message);
      changed();
    } catch (err) {
      toast.error(errorMessage(err, 'Re-check failed'));
    } finally {
      setBusy('');
    }
  };

  const bySource = totals.by_source || {};
  return (
    <section className="space-y-3" aria-labelledby="mapping-queue-title" data-testid="mapping-queue">
      <StatStrip testId="mapping-queue-stats" items={[
        { key: 'codes', label: 'codes waiting', value: totals.codes ?? '–', tone: totals.codes ? 'amber' : 'green', hint: 'Codes that matched no SKU, across API sales, Amazon returns and Retail POs', active: !source, onClick: () => setSource('') },
        { key: 'value', label: 'not counted yet', value: data ? inr(totals.value) : '–', tone: 'red', hint: 'Sales and PO value held by waiting codes; not in Sales, stock or profit until mapped' },
        { key: 'strong', label: 'strong suggestions', value: totals.strong ?? '–', tone: 'green', hint: 'Rows whose best suggestion is the same code or a strong match' },
        { key: 'sales', label: 'from sales', value: bySource.sales?.codes ?? 0, tone: 'amber', active: source === 'sales', onClick: () => setSource(source === 'sales' ? '' : 'sales') },
        { key: 'returns', label: 'from returns', value: bySource.returns?.codes ?? 0, tone: 'purple', active: source === 'returns', onClick: () => setSource(source === 'returns' ? '' : 'returns') },
        { key: 'po', label: 'from Retail POs', value: bySource.po?.codes ?? 0, tone: 'blue', active: source === 'po', onClick: () => setSource(source === 'po' ? '' : 'po') },
        { key: 'recent', label: 'mapped in 7 days', value: totals.mapped_recently ?? '–', tone: 'gray' },
      ]} />

      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
          <div>
            <h2 id="mapping-queue-title" className="text-sm font-semibold text-gray-900">Needs mapping</h2>
            <p className="text-xs text-gray-500">Largest value first. Accept saves the mapping (and the code's ASIN on that SKU) and moves its sales, returns and PO lines in at once.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && strongRows.length > 0 && (
              <Button size="sm" onClick={() => setAcceptAllOpen(true)} disabled={Boolean(busy)} data-testid="queue-accept-all">
                <CheckCheck className="mr-1 h-4 w-4" aria-hidden="true" /> Accept {strongRows.length} strong suggestion{strongRows.length === 1 ? '' : 's'}
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setExportOpen(true)} disabled={!data?.codes?.length} data-testid="queue-export">
              <Download className="mr-1 h-4 w-4" aria-hidden="true" /> Export for executives
            </Button>
            <Button size="sm" variant="outline" onClick={recheck} disabled={Boolean(busy)} data-testid="queue-recheck">
              <RefreshCw className={`mr-1 h-4 w-4 ${busy === 'recheck' ? 'animate-spin' : ''}`} aria-hidden="true" /> Re-check
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs">
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a code, title or PO…" aria-label="Find a waiting code" className="h-8 w-full text-xs sm:max-w-xs" data-testid="queue-search" />
          <label className="flex items-center gap-1.5 text-gray-700"><input type="checkbox" checked={onlySuggested} onChange={e => setOnlySuggested(e.target.checked)} /> With a suggestion</label>
          <label className="flex items-center gap-1.5 text-gray-700"><input type="checkbox" checked={showIgnored} onChange={e => setShowIgnored(e.target.checked)} /> Show ignored ({totals.ignored ?? 0})</label>
          <span className="ml-auto text-gray-600" data-testid="queue-count">{rows.length} of {data?.codes?.length ?? 0}</span>
        </div>
        {canEdit && selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-y border-blue-100 bg-blue-50 px-4 py-2 text-xs text-blue-900" role="status" data-testid="queue-bulk-bar">
            <span className="font-medium">{chosen.length} selected{chosen.length !== selected.size ? ` (${selected.size - chosen.length} hidden by the filter)` : ''}</span>
            <Button size="sm" variant="outline" className="h-7" onClick={() => setBulkOpen(true)} disabled={Boolean(busy)} data-testid="queue-map-selected">Map selected to one SKU</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={() => { setIgnoreTarget(null); setIgnoreOpen(true); }} disabled={Boolean(busy)} data-testid="queue-ignore-selected"><Ban className="mr-1 h-3.5 w-3.5" aria-hidden="true" />Ignore selected</Button>
            <button type="button" className="ml-auto underline" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        )}
        {error && <div className="p-4"><LoadError what="the mapping queue" error={error} onRetry={load} retrying={loading} testId="queue-load-error" /></div>}
        {!error && data && !rows.length && (
          <p className="p-8 text-center text-sm text-gray-500" data-testid="queue-empty">{data.codes.length ? 'Nothing matches the filter.' : 'Every code is mapped. Nothing is waiting.'}</p>
        )}
        {rows.length > 0 && (
          <div className={`relative overflow-x-auto ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            <table className="w-full text-left text-xs" data-testid="queue-table">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  {canEdit && <th className="w-8 px-3 py-2"><input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all codes on this page" /></th>}
                  <th className="px-3 py-2">Code · seen as</th>
                  <th className="px-3 py-2">Source</th>
                  <th className="px-3 py-2 text-right">Units</th>
                  <th className="px-3 py-2 text-right">Value</th>
                  <th className="px-3 py-2">Dates</th>
                  <th className="px-3 py-2">Suggested SKU</th>
                  <th className="px-3 py-2">Map to</th>
                  <th className="px-3 py-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {table.pageRows.map(row => <QueueRow key={row.code} row={row} country={country} canEdit={canEdit} skus={skus} selected={selected.has(row.code)} onToggle={() => toggle(row.code)}
                  onAccepted={changed} onIgnore={() => { setIgnoreTarget(row); setIgnoreOpen(true); }} onUnignore={() => unignore(row)} busy={Boolean(busy)} />)}
              </tbody>
            </table>
            <TablePager table={table} itemLabel="codes" className="border-t" />
          </div>
        )}
        {data?.codes?.some(r => r.sales?.unpriced_lines) && <p className="px-4 py-2 text-[11px] text-gray-500">+ some orders are still pending at Amazon and have no price yet.</p>}
      </div>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="queue-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Map {chosen.length} code{chosen.length === 1 ? '' : 's'} to one SKU</DialogTitle>
            <DialogDescription>Use this for listing variants of one product (GLB / FNB / GB prefixes, -RL suffixes). Their ASINs are recorded on the SKU too.</DialogDescription>
          </DialogHeader>
          <ul className="max-h-32 overflow-y-auto font-mono text-xs text-gray-700">{chosen.map(row => <li key={row.code}>{row.code}</li>)}</ul>
          <SkuPicker code="bulk" value={bulkSku} skus={skus} onPick={setBulkSku} disabled={Boolean(busy)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={mapSelected} disabled={!bulkSku || !chosen.length || Boolean(busy)} data-testid="queue-bulk-save">Map {chosen.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ignoreOpen} onOpenChange={open => { setIgnoreOpen(open); if (!open) setIgnoreTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="queue-ignore-dialog">
          <DialogHeader>
            <DialogTitle>Ignore {ignoreTarget ? ignoreTarget.code : `${chosen.length} code${chosen.length === 1 ? '' : 's'}`}?</DialogTitle>
            <DialogDescription>For codes that are not products (shipping lines, gift wrap, test listings). The rows stay stored and are not counted; un-ignore or map the code to bring them back.</DialogDescription>
          </DialogHeader>
          <Input value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)} placeholder="Reason (optional)" aria-label="Reason" maxLength={200} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIgnoreOpen(false)}>Cancel</Button>
            <Button onClick={ignore} disabled={busy === 'ignore'} data-testid="queue-ignore-save">{busy === 'ignore' ? 'Saving…' : 'Ignore'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} country={country} />

      <ConfirmDialog open={acceptAllOpen} onOpenChange={setAcceptAllOpen}
        title={`Accept ${strongRows.length} strong suggestion${strongRows.length === 1 ? '' : 's'}?`}
        description={`Each code shown in the current list is mapped to its suggested SKU (same code or a strong match) and its waiting sales, returns and PO lines move in. Worth ${inr(strongRows.reduce((sum, row) => sum + (row.value || 0), 0))}. Mappings can be edited or deleted below afterwards.`}
        confirmLabel="Accept all" busyLabel="Mapping…" onConfirm={acceptAllStrong} testId="queue-accept-all-confirm" />
    </section>
  );
};

// Choose all categories (one sheet each, for the brand manager) or one category's file (for its executive).
const ExportDialog = ({ open, onOpenChange, country }) => {
  const [cats, setCats] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setCats(null);
    axios.get(`${API}/queue/categories`, { params: { country }, headers: getAuthHeaders(), signal: controller.signal })
      .then(res => setCats(res.data.categories || []))
      .catch(err => { if (!controller.signal.aborted) { setCats([]); toast.error(errorMessage(err, 'Could not list the categories')); } });
    return () => controller.abort();
  }, [open, country]);
  const download = async category => {
    setBusy(category || 'ALL');
    try {
      const slug = category ? category.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() : 'all';
      const params = new URLSearchParams({ country, ...(category ? { category } : {}) });
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadFile(`${API}/queue/export?${params}`, `codes-to-map-${slug}-${stamp}.xlsx`);
      toast.success(`${category || 'All categories'}: file saved to your downloads`);
    } catch (err) {
      toast.error(errorMessage(err, 'Export failed'));
    } finally {
      setBusy('');
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="queue-export-dialog">
        <DialogHeader>
          <DialogTitle>Export codes to map</DialogTitle>
          <DialogDescription>
            Excel with category, brand, selling brand, the Amazon listing link and the suggested SKU. Executives fill
            <strong> Map To SKU</strong> (and Action); the brand manager ticks <strong>Checked by brand manager</strong>; then upload
            the file here with <strong>Upload filled file</strong>. Rows marked Checked = No or Action = Not sure are held back;
            Action = Ignore keeps a code out of the queue; rows without a SKU are skipped.
          </DialogDescription>
        </DialogHeader>
        <Button onClick={() => download('')} disabled={Boolean(busy)} aria-busy={busy === 'ALL'} className="w-full justify-between" data-testid="queue-export-all">
          <span>{busy === 'ALL' ? 'Preparing the file…' : 'All categories · one sheet each (for brand managers)'}</span>
          <Download className={`h-4 w-4 ${busy === 'ALL' ? 'animate-pulse' : ''}`} aria-hidden="true" />
        </Button>
        <p className="sr-only" role="status" aria-live="polite">{busy ? `Preparing ${busy === 'ALL' ? 'the all-categories' : busy} file` : ''}</p>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
          {!cats && <p className="p-3 text-xs text-gray-500" role="status">Working out categories…</p>}
          {cats && !cats.length && <p className="p-3 text-xs text-gray-500">Nothing to export.</p>}
          {cats?.map(cat => (
            <button key={cat.category} type="button" onClick={() => download(cat.category)} disabled={Boolean(busy)} aria-busy={busy === cat.category}
              className="flex w-full items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60"
              data-testid={`queue-export-${cat.category}`}>
              <span className="min-w-0 truncate font-medium text-gray-900">{cat.category}</span>
              <span className="flex shrink-0 items-center gap-3 text-xs text-gray-600 tabular-nums">
                {cat.codes} code{cat.codes === 1 ? '' : 's'} · {inr(cat.value)}
                <Download className={`h-3.5 w-3.5 ${busy === cat.category ? 'animate-pulse' : ''}`} aria-hidden="true" />
              </span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-500">Category comes from the suggested SKU, else SKUs of the same model or a similar code; the sheet says which. Codes with no similar SKU are under Unassigned.</p>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const QueueRow = ({ row, country, canEdit, skus, selected, onToggle, onAccepted, onIgnore, onUnignore, busy }) => {
  const [pick, setPick] = useState('');
  const [saving, setSaving] = useState(false);
  const mapTo = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/accept`, { code: row.code, sku_code: pick, country, asins: row.asins || [] }, { headers: getAuthHeaders() });
      toast.success(data.message);
      onAccepted();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save the mapping'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <tr className={`border-t border-gray-100 align-top ${row.ignored ? 'bg-gray-50 text-gray-500' : ''}`} data-testid={`queue-row-${row.code}`}>
      {canEdit && <td className="px-3 py-2">{!row.ignored && <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Select ${row.code}`} />}</td>}
      <td className="px-3 py-2">
        <div className="font-mono font-semibold text-gray-900">{row.code}</div>
        {row.alt_codes?.length > 0 && <div className="font-mono text-[11px] text-gray-500">{row.alt_codes.join(', ')}</div>}
        {row.title && <div className="max-w-[260px] truncate text-[11px] text-gray-500" title={row.title}>{row.title}</div>}
        {row.pos?.length > 0 && <div className="text-[11px] text-gray-500">{row.pos.slice(0, 3).join(', ')}{row.pos.length > 3 ? ` +${row.pos.length - 3}` : ''}</div>}
        {row.ignored && <div className="text-[11px] italic">Ignored{row.ignore_reason ? `: ${row.ignore_reason}` : ''}</div>}
      </td>
      <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{row.sources.map(s => <SourceChip key={s} source={s} />)}</div>
        <div className="mt-0.5 text-[11px] text-gray-500">{(row.platforms || []).map(platformLabel).join(', ')}</div></td>
      <td className="px-3 py-2 text-right tabular-nums">{Math.round(row.units).toLocaleString('en-IN')}</td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">{inr(row.value)}{row.sales?.unpriced_lines ? ' +' : ''}</td>
      <td className="whitespace-nowrap px-3 py-2">{dates(row)}</td>
      <td className="px-3 py-2">{row.ignored ? <span className="text-[11px]">—</span> : <SuggestionCell row={row} country={country} onAccepted={onAccepted} disabled={!canEdit || busy} />}</td>
      <td className="px-3 py-2">
        {canEdit && !row.ignored && (
          <div className="flex items-center gap-1">
            <SkuPicker code={row.code} value={pick} skus={skus} onPick={setPick} disabled={saving || busy} />
            <Button size="sm" variant="outline" className="h-8 px-2 text-[11px]" onClick={mapTo} disabled={!pick || saving || busy} data-testid={`queue-map-${row.code}`}>{saving ? 'Saving…' : 'Map'}</Button>
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right text-[11px]">
        {canEdit && (row.ignored
          ? <button type="button" onClick={onUnignore} className="inline-flex items-center gap-1 text-blue-700 underline"><Undo2 className="h-3 w-3" aria-hidden="true" />Un-ignore</button>
          : <button type="button" onClick={onIgnore} className="inline-flex items-center gap-1 text-gray-600 underline" title="Not a product: keep it out of the queue"><Ban className="h-3 w-3" aria-hidden="true" />Ignore</button>)}
        {!row.ignored && <> · <Link to={`/skus?create=${encodeURIComponent(row.code)}`} className="text-blue-700 underline">Add to SKU Master</Link></>}
      </td>
    </tr>
  );
};

export default MappingQueue;
